/* eslint-disable max-lines */
import React from 'react';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
} from '@explorer/core';
import {
  FieldCheckbox,
  FormFieldControl,
  FormSchemaReturn,
} from '@explorer/forms';
import {
  Modules,
  SupportedSystems,
  SUPPORTS_OVERRIDES,
} from '@explorer/forms/presets/forms/ModifyClient/types';
import { ControlClientInfoResponse } from '@explorer/open-api-client';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Grid, Stack } from '@mui/material';
import { FormikProps } from 'formik';
import { useIntl } from 'react-intl';

import { Chip, Table } from '@crucible-risk/security-applications';

import { FormValues } from '../formSchema';
import { EscalationContacts } from './MDR/EscalationContacts/EscalationContacts';
import { getOrderedConnectedAccounts } from './MDR/helpers';
import { IntegrationStatus } from './MDR/IntegrationStatus';
import { SentinelOneConnection } from './MDR/SentinelOneConnection';
import { useQueryIntegrations } from './MDR/useQueryIntegrations';
import { useQuerySentinelOneAccounts } from './MDR/useQuerySentinelOneAccounts';
import { OverrideMTC } from './MTC/OverrideMTC';
import { PrepurchaseDescription } from './PrepurchaseDescription';
import { WizerCreateAccount } from './SAT/WizerCreateAccount';
import { useGetColumns } from './useGetColumns';

interface DisplayModulesProps {
  activeClient: ControlClientInfoResponse;
  moduleInfo: Modules;
  formik: FormikProps<FormValues>;
  formId: string;
  schema: FormSchemaReturn<FormValues>;
  triggerSave: () => void;
}

interface OverrideModuleProps {
  moduleKey: string;
  formik: FormikProps<FormValues>;
  formId: string;
  fieldName: string;
}

export const OverrideModules = ({
  formik,
  formId,
  moduleKey,
  fieldName,
}: OverrideModuleProps) => {
  if (moduleKey === 'MTC') {
    return <OverrideMTC formik={formik} formId={formId} />;
  }

  const renderField = (props) => (
    <FieldCheckbox
      {...props}
      options={[
        {
          label: { id: `forms-presets.${moduleKey}` },
          name: moduleKey,
        },
      ]}
    />
  );

  return (
    <FormFieldControl
      key={fieldName}
      formId={formId}
      formik={formik}
      name={fieldName}
      renderField={renderField}
    />
  );
};

export const DisplayModules = ({
  activeClient,
  moduleInfo,
  formik,
  formId,
  schema,
  triggerSave,
}: DisplayModulesProps) => {
  const { formatMessage } = useIntl();
  const intl = useIntl();
  const { columns } = useGetColumns(formatMessage);

  const { data: integrationsData } = useQueryIntegrations({
    client_id: activeClient.client.client_id,
  });

  const { data: sentinelOneData } = useQuerySentinelOneAccounts({
    client_id: activeClient.client.client_id,
  });

  const renderModules = () => {
    const { modules } = moduleInfo;

    return Object.entries(modules).map(([key, supportedModules]) => {
      const rows = supportedModules
        .map((item, idx) => {
          // TODO: For phase 2 when other source systems are added, add them here.
          // This will allow for specific components to be shown for each source system.
          switch (item.sourceSystems) {
            case SupportedSystems.SALESFORCE:
            case SupportedSystems.STRIPE: {
              const { isTrial, isExpired, endDate } = item.featureSet;
              return {
                id: idx,
                name: formatMessage({
                  id: `forms-presets.${item.sourceSystems}`,
                }),
                type: isTrial
                  ? formatMessage({ id: 'forms-presets.trial' })
                  : formatMessage({ id: 'forms-presets.subscription' }),
                endDate: endDate ? `${new Date(endDate).toDateString()}` : '',
                expired: isExpired
                  ? formatMessage({ id: 'forms-presets.expired' })
                  : '-',
              };
            }
            case SupportedSystems.HUBSPOT:
            case SupportedSystems.EXPLORER:
              return null;
            default:
              return null;
          }
        })
        .filter(Boolean);

      // This should be true if purchased via Stripe or Salesforce
      const isActiveViaProperChannels = !!supportedModules.find(
        (module) =>
          module.sourceSystems !== SupportedSystems.EXPLORER &&
          !module.featureSet.isExpired,
      );

      const isActiveViaOverride = !!supportedModules.find(
        (module) => module.sourceSystems === SupportedSystems.EXPLORER,
      );

      const prepurchaseInfo = moduleInfo.prepurchasedModules?.[key];

      const overrideSupported = SUPPORTS_OVERRIDES[key];
      return (
        <Grid item xs={12} key={key} mb={1}>
          <Accordion sx={{ border: '1px solid #CAD3DB', borderRadius: '8px' }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls={`${key}-content`}
              id={`${key}-header`}
            >
              <Grid container gap={1} alignItems="center">
                {/* // todo change the title */}
                {formatMessage({ id: `forms-presets.title-${key}` })}
                {(isActiveViaProperChannels || isActiveViaOverride) && (
                  <Chip label="Active" color="success" size="small" />
                )}
              </Grid>
            </AccordionSummary>
            <AccordionDetails>
              {rows?.length > 0 && (
                <>
                  <Typography variant="bodyS">
                    {formatMessage({ id: 'forms-presets.source-systems' })}
                  </Typography>
                  <Table
                    rows={rows}
                    hideFooter
                    paginationMode="client"
                    loading={false}
                    error={false}
                    columns={columns}
                    renderEmptyText=""
                  />
                </>
              )}
              {!!prepurchaseInfo && (
                <PrepurchaseDescription prepurchaseInfo={prepurchaseInfo} />
              )}
              {overrideSupported ? (
                <Stack py={2} px={2}>
                  {isActiveViaProperChannels && (
                    <Typography variant="bodyS">
                      {formatMessage({
                        id: 'forms-presets.module-active-override-description',
                      })}
                    </Typography>
                  )}
                  <OverrideModules
                    formik={formik}
                    formId={formId}
                    moduleKey={key}
                    fieldName={schema.control_components.name}
                  />
                </Stack>
              ) : null}
              <Stack>
                {key === 'MDR' ? (
                  <>
                    {(() => {
                      const { orderedValidAccounts } =
                        getOrderedConnectedAccounts(sentinelOneData);
                      const isSentinelOneConnected =
                        orderedValidAccounts.length > 0;
                      const isIntuneOrDefenderConnected =
                        integrationsData?.intune?.isConnected ||
                        integrationsData?.microsoftDefender?.isConnected;

                      return (
                        <>
                          <SentinelOneConnection
                            clientId={activeClient.client.client_id}
                            isDisabled={isIntuneOrDefenderConnected}
                            disabledReason={intl.formatMessage({
                              id: 'mdr.sentinelone.disabled-microsoft-connected',
                            })}
                          />
                          <Stack py={2} px={2}>
                            <Typography variant="caption" color="grey.500">
                              {intl.formatMessage({
                                id: 'mdr.integration.separator',
                              })}
                            </Typography>
                          </Stack>
                          <IntegrationStatus
                            integration={integrationsData?.intune}
                            integrationName="Intune"
                            integrationLogo="/img/integrations/Intune.svg"
                            nameTranslationId="mdr.intune.name"
                            connectedTranslationId="mdr.intune.connected"
                            notConnectedTranslationId="mdr.intune.not-connected"
                            isDisabled={isSentinelOneConnected}
                          />
                          <IntegrationStatus
                            integration={integrationsData?.microsoftDefender}
                            integrationName="Microsoft Defender"
                            integrationLogo="/img/integrations/MicrosoftDefender.svg"
                            nameTranslationId="mdr.defender.name"
                            connectedTranslationId="mdr.defender.connected"
                            notConnectedTranslationId="mdr.defender.not-connected"
                            isDisabled={isSentinelOneConnected}
                          />
                        </>
                      );
                    })()}
                    {activeClient?.client?.active_components?.MDR && (
                      <EscalationContacts
                        clientId={activeClient.client.client_id}
                      />
                    )}
                  </>
                ) : null}
                {key === 'SAT' &&
                !activeClient?.client?.active_components?.SAT &&
                formik.values?.control_components?.SAT ? (
                  <WizerCreateAccount
                    activeClient={activeClient}
                    triggerSave={triggerSave}
                    triggerUncheck={() => {
                      formik.setFieldValue('control_components.SAT', false);
                    }}
                  />
                ) : null}
              </Stack>
            </AccordionDetails>
          </Accordion>
        </Grid>
      );
    });
  };

  return (
    <Grid item xs={12} mb={2}>
      {renderModules()}
    </Grid>
  );
};
